// src/services/backoffice/userService.ts

import { API_BASE_URL } from '../../config/api';

// Types for User Management
export interface User {
  id: number;
  email: string;
  user_type: 'personal' | 'commercial';
  is_blocked: boolean;
  blocked?: number;
  free_access: boolean;
  created_at: string;
  updated_at: string;
  ts?: string;
  name?: string;
  phone_number?: string;
  company_name?: string;
  verified?: number;
  current_subscription?: {
    plan_name: string;
    status: string;
    end_date?: string;
    monthly_price?: number;
    admin_granted?: number;   // 1 = גישה שהוענקה ידנית ע"י אדמין
    start_date?: string;
  } | null;
}

export interface UserDetails extends User {
  // ✅ שדות נוספים שמחזיר השרת
  name?: string; // השם של המשתמש
  phone_number?: string; // מספר טלפון
  company_name?: string; // שם חברה
  verified?: number; // האם מאומת (0/1)
  blocked?: number; // סטטוס חסימה (0/1)

  // שדות נוספים אופציונליים
  subscription_status?: 'trial' | 'active' | 'expired' | 'cancelled';
  subscription_end_date?: string;
  last_login?: string;
  calculations_count?: number;
}

export interface GetUsersParams {
  page?: number;
  limit?: number;
  search?: string;
  user_type?: 'personal' | 'commercial' | '';
  is_blocked?: boolean | null;
  free_access?: boolean | null;
  verified?: boolean | null;
  has_subscription?: string;  // '1' = עם מנוי, '0' = ללא, '' = הכל
}

export interface GetUsersResponse {
  users: User[];
  total: number;
  page: number;
  limit: number;
  totalPages: number;
}

export interface SaveUserData {
  id?: number; // ✅ הוספה תקינה
  email: string;
  name: string;
  phone_number: string;
  password: string;
  user_type: 'personal' | 'commercial';
  company_name: string;
  blocked: boolean;
  verified: boolean;
  free_access: boolean;
}

// Helper function to get session token
const getSessionToken = (): string | null => {
  const token = localStorage.getItem('backoffice_session_id');
  // console.log('מחפש session token:', token); // ✅ debug
  return token;
};

// Helper function to create FormData
const createFormData = (data: Record<string, any>): FormData => {
  const formData = new FormData();

  // Debug - what data do we receive?
  // console.log('createFormData - received data:', data);

  const sessionToken = getSessionToken();
  // console.log('Session token:', sessionToken);

  if (sessionToken) {
    formData.append('session_token', sessionToken);
  } else {
    console.error('❌ אין session token!');
  }

  // Debug each key-value pair being added
  Object.keys(data).forEach(key => {
    const value = data[key];
    if (value !== null && value !== undefined) {
      // console.log(`Adding to FormData: ${key} = ${value} (type: ${typeof value})`);
      formData.append(key, value.toString());
    }
  });

  // console.log('FormData נוצר עם הנתונים:', data);
  // console.log('Session token נוסף:', !!sessionToken);

  return formData;
};

// Get users list with filters and pagination
export const getUsers = async (params: GetUsersParams = {}): Promise<GetUsersResponse> => {
  const {
    page = 1,
    limit = 10,
    search = '',
    user_type = '',
    is_blocked = null,
    free_access = null
  } = params;

  // console.log('BEFORE: is_blocked =', is_blocked, 'type:', typeof is_blocked);

  const requestData = {
    page: page.toString(),
    limit: limit.toString(),
    search,
    user_type,
    ...(is_blocked !== null && { blocked: is_blocked ? "1" : "0" }),
    ...(free_access !== null && { free_access: free_access ? "1" : "0" }),
    ...(params.verified !== null && params.verified !== undefined && { verified: params.verified ? "1" : "0" }),
    ...(params.has_subscription && { has_subscription: params.has_subscription }),
  };

  // console.log('AFTER - requestData:', requestData);

  const formData = createFormData(requestData);

  try {
    const response = await fetch(`${API_BASE_URL}/Api/service/backoffice/get_users`, {
      method: 'POST',
      body: formData,
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const result = await response.json();

    if (result.res === 'OK') {
      return {
        users: result.data.users,
        total: result.data.pagination.total,
        page: result.data.pagination.page,
        limit: result.data.pagination.limit,
        totalPages: result.data.pagination.total_pages,
      };
    } else {
      throw new Error(result.msg || 'Failed to fetch users');
    }
  } catch (error) {
    console.error('Error fetching users:', error);
    throw error;
  }
};

// Get user details
export const getUserDetails = async (userId: number): Promise<UserDetails> => {
  const formData = createFormData({ user_id: userId });

  try {
    const response = await fetch(`${API_BASE_URL}/Api/service/backoffice/get_user_details`, {
      method: 'POST',
      body: formData,
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const result = await response.json();

    // console.log('🔥 getUserDetails raw result:', result);
    // console.log('🔥 getUserDetails result.data:', result.data);

    if (result.res === 'OK') {
      // FIX: Return result.data.user instead of result.data
      // console.log('🔥 Returning result.data.user:', result.data.user);
      return result.data.user;
    } else {
      throw new Error(result.msg || 'Failed to fetch user details');
    }
  } catch (error) {
    console.error('Error fetching user details:', error);
    throw error;
  }
};
// Save user (create new if id=0, update if id>0)
export const saveUser = async (userData: SaveUserData): Promise<User> => {
  const requestData = {
    id: userData.id || 0,
    name: userData.name || '',
    email: userData.email,
    phone_number: userData.phone_number || '',
    user_type: userData.user_type,
    company_name: userData.company_name || '',
    blocked: userData.blocked ? '1' : '0',
    verified: userData.verified ? '1' : '0',
    free_access: userData.free_access ? '1' : '0',
    ...(userData.password && { password: userData.password })
  };

  const formData = createFormData(requestData);

  try {
    const response = await fetch(`${API_BASE_URL}/Api/service/backoffice/save_user`, {
      method: 'POST',
      body: formData,
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const result = await response.json();

    if (result.res === 'OK') {
      return result.data;
    } else {
      throw new Error(result.msg || 'Failed to save user');
    }
  } catch (error) {
    console.error('Error saving user:', error);
    throw error;
  }
};

// Toggle user block status
export const toggleUserBlock = async (userId: number, newBlockedStatus: boolean): Promise<{ is_blocked: boolean }> => {
  const formData = createFormData({
    user_id: userId,
    blocked: newBlockedStatus.toString() // ✅ שולחים את הסטטוס החדש ישירות
  });

  try {
    const response = await fetch(`${API_BASE_URL}/Api/service/backoffice/toggle_user_block`, {
      method: 'POST',
      body: formData,
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const result = await response.json();

    if (result.res === 'OK') {
      // ✅ השרת מחזיר data.blocked, נמיר ל-is_blocked
      return {
        is_blocked: result.data.blocked === 1
      };
    } else {
      throw new Error(result.msg || 'Failed to toggle user block');
    }
  } catch (error) {
    console.error('Error toggling user block:', error);
    throw error;
  }
};

// Toggle user free access - ✅ מתוקן לפי תגובת השרת
export const toggleFreeAccess = async (userId: number, newFreeAccessStatus: boolean): Promise<{ free_access: boolean }> => {
  const formData = createFormData({
    user_id: userId,
    free_access: newFreeAccessStatus ? '1' : '0'  // ✅ המרה ישירה ל-1/0
  });

  // console.log('Toggle Free Access - שולח:', { userId, newFreeAccessStatus, free_access: newFreeAccessStatus ? '1' : '0' });

  try {
    const response = await fetch(`${API_BASE_URL}/Api/service/backoffice/toggle_free_access`, {
      method: 'POST',
      body: formData,
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const result = await response.json();
    // console.log('Toggle Free Access - תגובה:', result);

    if (result.res === 'OK') {
      return {
        free_access: result.data.free_access === 1
      };
    } else {
      throw new Error(result.msg || 'Failed to toggle free access');
    }
  } catch (error) {
    console.error('Error toggling free access:', error);
    throw error;
  }
};
// ===== גישה מוגבלת (הרשאת אדמין) =====
// end_date בפורמט 'YYYY-MM-DD HH:MM:SS'. הגישה/הפקיעה/התזכורת מנוהלות ע"י ה-Backend הקיים.
export const grantManualAccess = async (
  userId: number,
  endDate: string,
  adminNotes?: string
): Promise<void> => {
  const formData = createFormData({
    user_id: userId,
    end_date: endDate,
    ...(adminNotes ? { admin_notes: adminNotes } : {}),
  });

  const response = await fetch(`${API_BASE_URL}/Api/service/backoffice/grant_manual_access`, {
    method: 'POST',
    body: formData,
  });
  if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
  const result = await response.json();
  if (result.res !== 'OK') throw new Error(result.msg || 'Failed to grant manual access');
};

export const updateManualAccess = async (userId: number, endDate: string): Promise<void> => {
  const formData = createFormData({ user_id: userId, end_date: endDate });

  const response = await fetch(`${API_BASE_URL}/Api/service/backoffice/update_manual_access`, {
    method: 'POST',
    body: formData,
  });
  if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
  const result = await response.json();
  if (result.res !== 'OK') throw new Error(result.msg || 'Failed to update manual access');
};

export const revokeManualAccess = async (userId: number): Promise<void> => {
  const formData = createFormData({ user_id: userId });

  const response = await fetch(`${API_BASE_URL}/Api/service/backoffice/revoke_manual_access`, {
    method: 'POST',
    body: formData,
  });
  if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
  const result = await response.json();
  if (result.res !== 'OK') throw new Error(result.msg || 'Failed to revoke manual access');
};

// Export users to Excel
export const exportUsers = async (params: GetUsersParams = {}): Promise<any[]> => {
  const {
    search = '',
    user_type = '',
    is_blocked = null,
    free_access = null,
    verified = null,
    has_subscription = '',
  } = params;

  const requestData: Record<string, string> = {
    search,
    user_type,
    ...(is_blocked !== null && { blocked: is_blocked ? "1" : "0" }),
    ...(free_access !== null && { free_access: free_access ? "1" : "0" }),
    ...(verified !== null && { verified: verified ? "1" : "0" }),
    ...(has_subscription && { has_subscription }),
  };

  const formData = createFormData(requestData);

  try {
    const response = await fetch(`${API_BASE_URL}/Api/service/backoffice/export_users`, {
      method: 'POST',
      body: formData,
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const result = await response.json();

    if (result.res === 'OK') {
      return result.data.users;
    } else {
      throw new Error(result.msg || 'Failed to export users');
    }
  } catch (error) {
    console.error('Error exporting users:', error);
    throw error;
  }
};